import { type FC, useMemo, useRef } from 'react';
import { useScheduledMeetingsMutations } from '../../contexts';
import { useToastHelpers } from '../../hooks/useToast';
import { getBaseUrl } from '../../utils/url';
import ToastContainer from '../Toast/ToastContainer';
import { PERSONAL_MEETINGS_MODAL_ID } from './helpers/constants';
import useDefaultMeeting from './hooks/useDefaultMeeting';
import useDeleteConfirmation from './hooks/useDeleteConfirmation';
import useMeetingMutations from './hooks/useMeetingMutations';
import useMeetingPermissions from './hooks/useMeetingPermissions';
import useMeetingsQuery from './hooks/useMeetingsQuery';
import useModalBehavior from './hooks/useModalBehavior';
import useSelectedMeeting from './hooks/useSelectedMeeting';
import ModalFooter from './nested/layout/ModalFooter';
import ModalHeader from './nested/layout/ModalHeader';
import Sidebar from './nested/layout/Sidebar';
import DeleteConfirmation from './nested/shared/DeleteConfirmation';
import DeleteMeetingButton from './nested/shared/DeleteMeetingButton';
import { EmptyState } from './nested/shared/EmptyState';
import MeetingTabs from './nested/tabs/MeetingTabs';
import type { PersonalMeetingsModalProps } from './types';

const PersonalMeetingsModal: FC<PersonalMeetingsModalProps> = ({ isOpen = true, onClose }) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  useModalBehavior({ isOpen, onClose, modalRef });
  const toastHelpers = useToastHelpers();

  const { personalMeetings, isLoading, isFetching, queryError } = useMeetingsQuery();
  const { isCreating, isUpdating, deletingRoom, ...mutations } = useScheduledMeetingsMutations();
  const { apiReady, canCreateMore, creationLimit, isFreePlan, permissions } =
    useMeetingPermissions(personalMeetings);

  const { defaultMeetingId, setDefaultMeetingId, isDefaultMeeting } = useDefaultMeeting({
    toastHelpers,
  });

  const { selectedMeeting, meetingData, selectMeetingHandler } = useSelectedMeeting(
    personalMeetings,
    defaultMeetingId,
  );

  const { createMeeting, deleteMeeting, updateMeetingData, toggleDefaultMeeting } =
    useMeetingMutations({
      personalMeetings,
      selectedMeeting,
      meetingData,
      userPermissions: permissions,
      onRoomChange: selectMeetingHandler,
      toastHelpers,
      setDefaultMeetingId,
      isDefaultMeeting,
      createMeetingMutation: mutations.create,
      updateMeetingMutation: mutations.update,
      deleteMeetingMutation: mutations.remove,
      maxPersonalMeetings: creationLimit.max,
    });

  const { meetingToDelete, isProcessing, initiateDelete, confirmDelete, cancelDelete } =
    useDeleteConfirmation({ deleteMeeting, deletingRoom: deletingRoom ?? null });

  // Delete is rendered into both the tab row and the footer so a container query can pick one.
  // The confirmation state stays here: moving it back into the button would let a slot being
  // hidden unmount an open confirmation dialog.
  const deleteButton =
    selectedMeeting && !isDefaultMeeting(selectedMeeting.room) ? (
      <DeleteMeetingButton
        onDelete={() => initiateDelete(selectedMeeting)}
        isProcessing={isProcessing}
      />
    ) : null;

  const createDisabledReason = useMemo(() => {
    if (!apiReady) {
      return 'Loading account permissions…';
    }
    if (!permissions.canScheduleMeetings) {
      return 'You do not have permission to create meetings.';
    }
    if (isFreePlan) {
      return 'Personal meetings are not supported on a free plan.';
    }
    if (!canCreateMore) {
      return `You can create up to ${creationLimit.max} Personal Meeting IDs.`;
    }
    return null;
  }, [apiReady, canCreateMore, creationLimit.max, isFreePlan, permissions.canScheduleMeetings]);

  const canCreateMeeting = apiReady && permissions.canScheduleMeetings && canCreateMore;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="personal-meetings-modal" id={PERSONAL_MEETINGS_MODAL_ID}>
      <div className="personal-meetings-modal__dialog" ref={modalRef}>
        <ModalHeader
          totalMeetings={personalMeetings.length}
          limit={creationLimit.max}
          isFreePlan={isFreePlan}
          onClose={onClose}
          isUpdating={isUpdating}
        />

        <div className="personal-meetings-modal__layout">
          <Sidebar
            meetings={personalMeetings}
            selectedRoom={selectedMeeting?.room ?? null}
            onSelect={selectMeetingHandler}
            onCreate={createMeeting}
            isCreating={isCreating}
            canCreate={canCreateMeeting}
            creationDisabledReason={createDisabledReason}
            isFreePlan={isFreePlan}
            baseJoinUrl={getBaseUrl()}
            defaultMeetingId={defaultMeetingId}
          />

          <div className="personal-meetings-modal__main">
            {meetingData && selectedMeeting ? (
              <MeetingTabs
                selectedMeeting={selectedMeeting}
                selectedMeetingData={meetingData}
                isUpdating={isUpdating}
                updateMeetingData={updateMeetingData}
                toggleDefaultMeeting={toggleDefaultMeeting}
                tabRowAction={deleteButton}
              />
            ) : (
              <EmptyState
                isLoading={isLoading}
                hasMeetings={personalMeetings.length > 0}
                isFreePlan={isFreePlan}
              />
            )}

            <ModalFooter
              queryError={queryError}
              isFetching={isFetching}
              onClose={onClose}
              leadingAction={deleteButton}
            />

            {meetingToDelete && (
              <DeleteConfirmation
                meetingTitle={meetingToDelete.description || meetingToDelete.room}
                isProcessing={isProcessing}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
              />
            )}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toastHelpers.toasts} onRemoveToast={toastHelpers.removeToast} />
    </div>
  );
};

export default PersonalMeetingsModal;
